import { useEffect, useState } from "react";
import client from "../api/client";

const today = () => new Date().toISOString().split("T")[0];

const styles = {
  page: { padding: 0, fontSize: 11 },
  card: {
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 700 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: { height: 30, fontSize: 11, borderRadius: 7, fontWeight: 600 },
  stat: {
    padding: 9,
    borderRadius: 10,
    fontSize: 11,
    border: "1px solid #e9ecef",
  },
  statValue: { fontSize: 14, fontWeight: 700 },
  tableWrap: {
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, marginBottom: 0, minWidth: 900 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    color: "#fff",
    whiteSpace: "nowrap",
  },
  td: {
    fontSize: 11,
    padding: "6px 8px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
};

export default function CashBookPage() {
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({
    fromDate: today(),
    toDate: today(),
    accountId: "",
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const getVal = (obj, ...keys) => {
    for (const key of keys) {
      if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
    }
    return "";
  };

  const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const formatDate = (value) => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN");
  };

  const loadAccounts = async () => {
    try {
      const res = await client.get("/accounts");
      setAccounts(res.data || []);
    } catch {
      alert("Failed to load accounts");
    }
  };

  const loadCashBook = async () => {
    const accountId = Number(filter.accountId);

    if (!accountId || accountId <= 0) {
      alert("Please select valid Cash/Bank account");
      return;
    }

    try {
      const res = await client.get(
        `/finance/cash-book?fromDate=${filter.fromDate}&toDate=${filter.toDate}&accountId=${accountId}`,
      );
      setRows(res.data || []);
    } catch {
      alert("Failed to load cash book");
    }
  };

  const totalReceipt = rows.reduce(
    (sum, r) => sum + num(getVal(r, "Receipt", "receipt")),
    0,
  );

  const totalPayment = rows.reduce(
    (sum, r) => sum + num(getVal(r, "Payment", "payment")),
    0,
  );

  const closingBalance =
    rows.length > 0
      ? num(getVal(rows[rows.length - 1], "Balance", "balance"))
      : 0;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>
              <i className="bi bi-cash-stack me-2"></i>
              Cash Book
            </div>
            <div style={styles.sub}>Cash / Bank receipt and payment report</div>
          </div>

          <span
            className="badge bg-light text-primary"
            style={{ fontSize: 10 }}
          >
            Running Balance
          </span>
        </div>

        <div style={styles.body}>
          <div className="row g-2 mb-2">
            <Field label="From Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.fromDate}
                onChange={(e) =>
                  setFilter({ ...filter, fromDate: e.target.value })
                }
              />
            </Field>

            <Field label="To Date" className="col-md-3">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={filter.toDate}
                onChange={(e) =>
                  setFilter({ ...filter, toDate: e.target.value })
                }
              />
            </Field>

            <Field label="Cash / Bank Account" className="col-md-4">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={filter.accountId}
                onChange={(e) =>
                  setFilter({ ...filter, accountId: e.target.value })
                }
              >
                <option value="">Select Account</option>
                {accounts.map((a, index) => {
                  const id = getVal(
                    a,
                    "AccountId",
                    "accountId",
                    "AccountID",
                    "Id",
                    "id",
                  );
                  const name = getVal(
                    a,
                    "AccountName",
                    "accountName",
                    "ACCOUNTNAME",
                    "Name",
                    "name",
                  );
                  const code = getVal(a, "AccountCode", "accountCode");

                  return (
                    <option key={id || index} value={id}>
                      {code ? `${code} - ${name}` : name}
                    </option>
                  );
                })}
              </select>
            </Field>

            <div className="col-md-2 d-flex align-items-end">
              <button
                onClick={loadCashBook}
                className="btn btn-primary btn-sm w-100"
                style={styles.btn}
              >
                <i className="bi bi-search me-1"></i>
                Load
              </button>
            </div>
          </div>

          <div className="row g-2 mb-2">
            <Stat
              title="Total Receipt"
              value={`₹ ${totalReceipt.toFixed(2)}`}
              bg="#e7f1ff"
            />
            <Stat
              title="Total Payment"
              value={`₹ ${totalPayment.toFixed(2)}`}
              bg="#fff3cd"
            />
            <Stat
              title="Closing Balance"
              value={`₹ ${Math.abs(closingBalance).toFixed(2)} ${
                closingBalance >= 0 ? "Dr" : "Cr"
              }`}
              bg="#e9f7ef"
            />
          </div>

          <div style={styles.tableWrap}>
            <table
              className="table table-bordered table-hover align-middle"
              style={styles.table}
            >
              <thead>
                <tr>
                  {[
                    "Date",
                    "Voucher No",
                    "Type",
                    "Particulars",
                    "Receipt",
                    "Payment",
                    "Balance",
                  ].map((h) => (
                    <th
                      key={h}
                      style={styles.th}
                      className={
                        ["Receipt", "Payment", "Balance"].includes(h)
                          ? "text-end"
                          : ""
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center text-muted"
                      style={{ ...styles.td, padding: 18 }}
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, index) => {
                    const entryDate = getVal(
                      r,
                      "EntryDate",
                      "entryDate",
                      "VoucherDate",
                      "voucherDate",
                    );
                    const voucherNo = getVal(r, "VoucherNo", "voucherNo");
                    const voucherType = getVal(
                      r,
                      "VoucherType",
                      "voucherType",
                      "Type",
                      "type",
                    );
                    const particulars = getVal(
                      r,
                      "Particulars",
                      "particulars",
                      "Description",
                      "description",
                    );
                    const receipt = num(getVal(r, "Receipt", "receipt"));
                    const payment = num(getVal(r, "Payment", "payment"));
                    const balance = num(getVal(r, "Balance", "balance"));

                    return (
                      <tr key={index}>
                        <td style={styles.td}>{formatDate(entryDate)}</td>
                        <td style={{ ...styles.td, fontWeight: 700 }}>
                          {voucherNo}
                        </td>
                        <td style={styles.td}>{voucherType}</td>
                        <td style={styles.td}>{particulars}</td>
                        <td className="text-end" style={styles.td}>
                          {receipt > 0 ? receipt.toFixed(2) : ""}
                        </td>
                        <td className="text-end" style={styles.td}>
                          {payment > 0 ? payment.toFixed(2) : ""}
                        </td>
                        <td
                          className="text-end"
                          style={{
                            ...styles.td,
                            fontWeight: 700,
                            color: "#0d6efd",
                          }}
                        >
                          {Math.abs(balance).toFixed(2)}{" "}
                          {balance >= 0 ? "Dr" : "Cr"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>

              {rows.length > 0 && (
                <tfoot>
                  <tr style={{ background: "#f8f9fa", fontWeight: 700 }}>
                    <td colSpan="4" className="text-end" style={styles.td}>
                      Total
                    </td>
                    <td className="text-end" style={styles.td}>
                      {totalReceipt.toFixed(2)}
                    </td>
                    <td className="text-end" style={styles.td}>
                      {totalPayment.toFixed(2)}
                    </td>
                    <td className="text-end" style={styles.td}>
                      {Math.abs(closingBalance).toFixed(2)}{" "}
                      {closingBalance >= 0 ? "Dr" : "Cr"}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function Stat({ title, value, bg }) {
  return (
    <div className="col-md-4">
      <div style={{ ...styles.stat, background: bg }}>
        <b>{title}</b>
        <div style={styles.statValue}>{value}</div>
      </div>
    </div>
  );
}
